"use client";

import { useCallback, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import GradientBlobContainer from "@/components/ui/container/GradientBlobContainer";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button/Button";
import { HomeBrandLogo } from "@/components/home/HomeBrandLogo.tsx";
import type { WorkspaceWelcomeScreenConfig } from "@/types/workspaceWelcome";
import {
  AnimatedWelcomeGreeting,
  welcomeGreetingRevealDurationMs,
} from "./AnimatedWelcomeGreeting";
import { WelcomeWorkflowProgress } from "./WelcomeWorkflowProgress";
import { formatAdviserTimeGreeting } from "@/utils/adviserPersonaName";

function timeOfDayGreeting(now = new Date()): string {
  const hour = now.getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

const WELCOME_GLASS_PANEL_CLASS =
  "w-full rounded-[28px] bg-white/[0.03] px-6 py-8 text-center shadow-[0_2px_24px_rgba(0,0,0,0.12)] backdrop-blur-xl backdrop-saturate-125 sm:px-8 sm:py-10";

const INTRO_EXIT_TRANSITION = { duration: 0.5, ease: [0.22, 1, 0.36, 1] as const };

export type WorkspaceWelcomeScreenProps = {
  config: WorkspaceWelcomeScreenConfig;
  personaName?: string;
  companyLogoUrl?: string | null;
  /** Optional hint pointing users to the Ani dock button (OpenWork demo). */
  aniHint?: string | null;
  onGetStarted: () => void;
};

/** Full OS-workspace welcome overlay — shown until the adviser continues to home. */
export function WorkspaceWelcomeScreen({
  config,
  personaName,
  companyLogoUrl,
  aniHint,
  onGetStarted,
}: WorkspaceWelcomeScreenProps) {
  const reduceMotion = useReducedMotion();
  const [isExiting, setIsExiting] = useState(false);
  const greeting = timeOfDayGreeting();
  const resolvedName = personaName?.trim() ?? "";
  const greetingLine = formatAdviserTimeGreeting(greeting, resolvedName);
  const bodyCopyDelay = welcomeGreetingRevealDurationMs(greetingLine.length) / 1000 + 0.15;

  const handleGetStarted = useCallback(() => {
    if (isExiting) return;
    if (reduceMotion) {
      onGetStarted();
      return;
    }
    setIsExiting(true);
  }, [isExiting, onGetStarted, reduceMotion]);

  const handleExitComplete = useCallback(() => {
    if (isExiting) onGetStarted();
  }, [isExiting, onGetStarted]);

  return (
    <motion.div
      className="pointer-events-auto absolute inset-0 z-[10050] flex min-h-0 flex-col overflow-hidden bg-slate-950"
      data-testid="workspace-welcome-screen"
      initial={false}
      animate={{ opacity: isExiting ? 0 : 1 }}
      transition={INTRO_EXIT_TRANSITION}
      onAnimationComplete={handleExitComplete}
    >
      <motion.div
        className="pointer-events-none absolute inset-0 opacity-50"
        aria-hidden
        initial={false}
        animate={{ opacity: isExiting ? 0 : 0.5 }}
        transition={INTRO_EXIT_TRANSITION}
      >
        <GradientBlobContainer
          seed="workspace-welcome"
          dark
          position="bl"
          className="h-full w-full"
          eager
          rasterize
          themeColors={["#312e81", "#1e3a8a", "#0f172a", "#4c1d95"]}
        />
      </motion.div>
      <motion.div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(99,102,241,0.12)_0%,transparent_55%)]"
        aria-hidden
        initial={false}
        animate={{ opacity: isExiting ? 0 : 1 }}
        transition={INTRO_EXIT_TRANSITION}
      />

      <div className="relative flex min-h-0 flex-1 flex-col items-center justify-center px-6 py-10">
        <motion.div
          className="flex w-full max-w-2xl flex-col items-center"
          initial={{ opacity: 0, y: 18 }}
          animate={
            isExiting
              ? { opacity: 0, y: -14, scale: 0.985 }
              : { opacity: 1, y: 0, scale: 1 }
          }
          transition={isExiting ? INTRO_EXIT_TRANSITION : { duration: 0.55, ease: "easeOut" }}
        >
          <div className={WELCOME_GLASS_PANEL_CLASS} data-testid="workspace-welcome-glass-panel">
            <Logo
              className="mx-auto mb-8 justify-center [&>div:first-child]:h-11 [&>div:last-child]:w-[4.5rem] sm:[&>div:first-child]:h-12 sm:[&>div:last-child]:w-20"
              data-testid="workspace-welcome-ani-logo"
            />

            <AnimatedWelcomeGreeting greeting={greeting} personaName={resolvedName} />
            <motion.p
              className="mt-5 text-sm leading-relaxed text-slate-400 sm:text-[15px]"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: bodyCopyDelay, duration: 0.65, ease: "easeOut" }}
            >
              {config.body}
            </motion.p>

            {aniHint ? (
              <motion.p
                className="mt-3 text-xs leading-relaxed text-indigo-200/80 sm:text-sm"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: bodyCopyDelay + 0.1, duration: 0.6, ease: "easeOut" }}
                data-testid="workspace-welcome-ani-hint"
              >
                {aniHint}
              </motion.p>
            ) : null}

            <WelcomeWorkflowProgress steps={config.steps} startDelay={bodyCopyDelay + 0.2} />

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: bodyCopyDelay + 0.35, duration: 0.65, ease: "easeOut" }}
            >
              <Button
                color="indigo"
                size="lg"
                className="mt-10 min-w-[10rem] shadow-lg shadow-indigo-500/25"
                onClick={handleGetStarted}
                disabled={isExiting}
                data-testid="workspace-welcome-get-started"
              >
                Get started
              </Button>
            </motion.div>
          </div>

          {companyLogoUrl ? (
            <HomeBrandLogo
              logoUrl={companyLogoUrl}
              className="mt-8 h-8 w-auto max-w-[11rem] object-contain opacity-90 sm:h-9 sm:max-w-none"
              testId="workspace-welcome-company-logo"
            />
          ) : null}
        </motion.div>
      </div>
    </motion.div>
  );
}
